import type { QueryClient } from "@tanstack/react-query";
import type {
  HubStatus,
  Investor,
  InvestorListResponse,
  InvestorStatus,
  InvestorWithInvestmentSummary,
} from "@/domain/investor";
import { InvalidationEntity } from "../entityDependencies";
import { queryKeys } from "../queryKeys";
import { detailSlot, isObject, listSlot, type Merge } from "../cache/cacheSlots";
import { aggregateSlot, flatAggregates } from "../cache/aggregateSlots";
import { defineEntityCache } from "../cache/entityCache";
import {
  anyOf,
  byUuid,
  distinctPages,
  filled,
  listedStatus,
  overlaps,
  viewSlot,
  type ViewRules,
  type ViewShape,
} from "../cache/listViews";

export interface InvestorPatch {
  status?: InvestorStatus;
  hub_status?: HubStatus;
  /** The investor as the server answered it. */
  investor?: Investor;
}

type InvestorPage = Pick<InvestorListResponse, "investors" | "total_count">;

type Row = InvestorWithInvestmentSummary;

const page: ViewShape<InvestorPage, Row, string> = {
  isPage: (value): value is InvestorPage =>
    isObject(value) && Array.isArray(value.investors) && typeof value.total_count === "number",
  records: (current) => current.investors,
  withRecords: (current, investors, countDelta) => ({
    ...current,
    investors,
    total_count: current.total_count + countDelta,
  }),
  idOf: (record) => record.uuid,
  total: (current) => current.total_count,
};

export const distinctInvestorPages = distinctPages(page);

const amount = (value: string | null | undefined) => (value == null ? null : Number(value));
const time = (value: string) => Date.parse(value);

export const investorRules: ViewRules<Row> = {
  accepts: {
    status: listedStatus((row) => row.status),
    tag: overlaps((row) => row.tags.map((tag) => tag.uuid)),
    investor_type: anyOf((row) => row.type),
  },
  narrowing: ["search"],
  opaque: {
    search: (row) => [
      row.first_name,
      row.last_name,
      row.company_name,
      row.email,
      row.tax_identifier,
    ],
  },
  order: {
    created_at: (row) => time(row.created_at),
    "money.potential": (row) => amount(row.money.potential),
    number_investments: (row) => row.number_investments,
    "money.investment.committed": (row) => amount(row.money.investment.committed),
    "money.available": (row) => amount(row.money.available),
  },
  tieBreak: byUuid("asc", (row) => row.uuid),
};

const investorAggregates = flatAggregates<Row>({
  sums: {
    "money.potential:sum": (row) => row.money.potential,
    "number_investments:sum": (row) => row.number_investments,
    "money.investment.reserved:sum": (row) => row.money.investment.reserved,
    "money.investment.completed:sum": (row) => row.money.investment.completed,
    "money.investment.confirmed:sum": (row) => row.money.investment.confirmed,
    "money.investment.committed:sum": (row) => row.money.investment.committed,
    "money.available:sum": (row) => row.money.available,
  },
  counts: {
    "status:active": (row) => row.status === "active",
    "status:inactive": (row) => row.status === "inactive",
    "status:archived": (row) => row.status === "archived",
    "email:filled": (row) => filled(row.email),
    "email:empty": (row) => !filled(row.email),
    "city:filled": (row) => filled(row.city),
    "city:empty": (row) => !filled(row.city),
  },
  distributions: {
    "type:distribution": (row) => [row.type],
    "tag:distribution": (row) => row.tags.map((tag) => tag.uuid),
  },
  ratios: { "status:pct_active": (row) => row.status === "active" },
  emptyDistribution: {},
});

/** The investors list and its footers, for any change that reaches a list row. */
export function investorListSlots<TPatch>(orgId: string, merge: Merge<Row, TPatch>) {
  return [
    viewSlot({
      prefix: queryKeys.investors.lists(orgId),
      shape: page,
      merge,
      parse: queryKeys.investors.parseList,
      rules: investorRules,
    }),
    aggregateSlot<Record<string, unknown>, Row, string, TPatch>({
      prefix: queryKeys.investors.aggregatesAll(orgId),
      rules: investorRules,
      parse: queryKeys.investors.parseAggregates,
      ...investorAggregates,
      sizeFrom: {
        prefix: queryKeys.investors.lists(orgId),
        parse: queryKeys.investors.parseList,
        total: (current) => (page.isPage(current) ? current.total_count : undefined),
      },
    }),
  ];
}

function isInvestor(value: unknown): value is Investor {
  return (
    isObject(value) &&
    typeof value.uuid === "string" &&
    (value.type === "individual" || value.type === "company") &&
    typeof value.hub_status === "string"
  );
}

const rowFields = (investor: Investor): Partial<InvestorWithInvestmentSummary> => ({
  status: investor.status,
  tags: investor.tags,
  email: investor.email,
  phone: investor.phone,
  city: investor.city,
  country: investor.country,
  taxable_income_base: investor.taxable_income_base,
  potential_investment_amount: investor.potential_investment_amount,
  cultural_interests: investor.cultural_interests,
  display_name: investor.display_name,
  tax_identifier: investor.tax_identifier,
  ...(investor.type === "individual"
    ? { first_name: investor.first_name, last_name: investor.last_name }
    : { company_name: investor.company_name }),
});

const mergeRow = (
  record: InvestorWithInvestmentSummary,
  patch: InvestorPatch,
): InvestorWithInvestmentSummary => ({
  ...record,
  ...(patch.investor ? rowFields(patch.investor) : {}),
  ...(patch.status ? { status: patch.status } : {}),
});

const mergeRecord = (record: Investor, patch: InvestorPatch): Investor =>
  patch.investor ?? {
    ...record,
    ...(patch.status ? { status: patch.status } : {}),
    ...(patch.hub_status ? { hub_status: patch.hub_status } : {}),
  };

export const investorCache = defineEntityCache<string, InvestorPatch, Row>({
  entity: InvalidationEntity.INVESTOR,
  row: mergeRow,
  slots: (orgId) => [
    ...investorListSlots(orgId, mergeRow),
    listSlot({ prefix: queryKeys.investorDropdown.all(orgId), shape: page, merge: mergeRow }),
    detailSlot({
      prefix: queryKeys.investors.records(orgId),
      isRecord: isInvestor,
      idOf: (record) => record.uuid,
      merge: mergeRecord,
    }),
  ],
  records: (orgId, uuid) => [
    queryKeys.investors.record(orgId, uuid),
    queryKeys.investors.documents(orgId, uuid),
  ],
});

/**
 * The investor as a list row already shows it, to open its record at once.
 * Fields the row does not carry stay empty until the record arrives.
 */
function seedFromRow(row: InvestorWithInvestmentSummary): Investor {
  const base = {
    uuid: row.uuid,
    organization_id: 0,
    status: row.status,
    tags: row.tags,
    email: row.email,
    phone: row.phone,
    city: row.city,
    country: row.country,
    communication_language: "es" as const,
    taxable_income_base: row.taxable_income_base,
    potential_investment_amount: row.potential_investment_amount,
    cultural_interests: row.cultural_interests ?? [],
    display_name: row.display_name,
    tax_identifier: row.tax_identifier ?? "",
    hub_status: "none" as const,
    hub_invitation_uuid: null,
    created_at: "",
  };
  if (row.type === "company")
    return {
      ...base,
      type: "company",
      company_name: row.company_name ?? row.display_name,
      tax_id: row.tax_identifier ?? "",
      signature_regime: "sole",
      legal_representatives: [],
    };
  return {
    ...base,
    type: "individual",
    first_name: row.first_name ?? "",
    last_name: row.last_name ?? "",
    national_id: row.tax_identifier ?? "",
    full_name: row.display_name,
  };
}

export function findCachedInvestor(
  client: QueryClient,
  orgId: string,
  uuid: string,
): Investor | undefined {
  for (const [, data] of client.getQueriesData({ queryKey: queryKeys.investors.lists(orgId) })) {
    const pages = isObject(data) && Array.isArray(data.pages) ? data.pages : [data];
    for (const current of pages) {
      if (!page.isPage(current)) continue;
      const found = current.investors.find((record) => record.uuid === uuid);
      if (found) return seedFromRow(found);
    }
  }
  return undefined;
}
